import { FC, useMemo } from 'react';
import { Select } from 'antd';
import { Entity, RecordGroup } from '#types/System.types';
import { useGetEntityGroups } from '../../use/useGetEntityGroups';

interface EntityGroupSelection {
  entityId: Entity['docId'];
  entityGroupId: RecordGroup['entityGroupId'] | null;
}

interface EntityGroupSelectorProps {
  value: EntityGroupSelection | null;
  onChange: (value: EntityGroupSelection | null) => void;
  style?: React.CSSProperties;
  limit?: number;
}

/**
 * Component for selecting an entity or entity group
 */
const EntityGroupSelector: FC<EntityGroupSelectorProps> = ({
  value,
  onChange,
  style,
  limit = 1000
}) => {
  const { groupOptions, loading } = useGetEntityGroups({ limit });

  const handleChange = (selectedValue: string | null) => {
    if (!selectedValue) {
      onChange(null);
      return;
    }
    try {
      const parsed = JSON.parse(selectedValue) as EntityGroupSelection;
      onChange(parsed);
    } catch (e) {
      console.error('Error parsing selection value:', e);
      onChange(null);
    }
  };

  const stringValue = value ? JSON.stringify(value) : null;

  return (
    <Select
      variant="filled"
      placeholder="Select Record Type"
      style={{ ...style }}
      styles={{
        popup: {
          root: {
            width: 320
          }
        }
      }}
      value={stringValue}
      onChange={handleChange}
      options={groupOptions}
      loading={loading}
      showSearch={{ optionFilterProp: 'label' }}
    />
  );
};

interface EntityGroupMultiSelectorProps {
  value: EntityGroupSelection[] | null;
  onChange: (value: EntityGroupSelection[] | null) => void;
  entityId: Entity['docId'];
  style?: React.CSSProperties;
  limit?: number;
  disabled?: boolean;
  /** Cap on inline tags; omit to show every selection (the Select grows vertically). */
  maxTagCount?: number | 'responsive';
}

/**
 * Component for selecting multiple entity groups
 */
const EntityGroupMultiSelector: FC<EntityGroupMultiSelectorProps> = ({
  value,
  onChange,
  entityId,
  style,
  limit = 1000,
  disabled = false,
  maxTagCount
}) => {
  const { groupOptions, loading, entities } = useGetEntityGroups({ limit });
  
  const filteredOptions = useMemo(() => {
    if (!entities || !entityId) return [];
    
    const entityGroupOption = groupOptions.find(group => 
      group.label === `${entities.find(e => e.docId === entityId)?.description?.shortLabel || entityId} Groups`
    );
    
    return entityGroupOption?.options || [];
  }, [groupOptions, entities, entityId]);

  const handleChange = (selectedValues: string[] | null) => {
    if (!selectedValues || selectedValues.length === 0) {
      onChange(null);
      return;
    }
    
    try {
      const parsedValues = selectedValues.map(val => JSON.parse(val) as EntityGroupSelection);
      onChange(parsedValues);
    } catch (e) {
      console.error('Error parsing selection values:', e);
      onChange(null);
    }
  };

  const stringValues = value ? value.map(item => JSON.stringify(item)) : [];

  return (
    <Select
      mode="multiple"
      size="small"
      variant="filled"
      placeholder="Select Entity Groups"
      style={{ ...style }}
      styles={{
        popup: {
          root: {
            width: 320
          }
        }
      }}
      value={stringValues}
      onChange={handleChange}
      options={filteredOptions}
      loading={loading}
      showSearch={{ optionFilterProp: 'label' }}
      disabled={disabled}
      allowClear
      maxTagCount={maxTagCount}
      maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} more`}
    />
  );
};

export default EntityGroupSelector;
export { EntityGroupMultiSelector };
